import { useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Information } from '@mint-lib/icons';
import { typographyVariants } from '@mint-lib/tw-components';
import {
  Autocomplete,
  AutocompleteContent,
  AutocompleteInput,
  AutocompleteTrigger,
} from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import type { GranularityLevel, SystemMetric } from '../../api/types.js';
import { useLocalizedMetricTypesLabels } from '../../constants/index.js';
import { useImportHubV2 } from '../../hooks/ff/index.js';
import { ManageCustomMetricsButton } from '../atoms/ManageCustomMetricsButton.jsx';
import { Tooltip } from '../atoms/Tooltip.jsx';

// A headers-meta metric shown in the dropdown
export type HeaderOption = Omit<SystemMetric, 'headerType'> & {
  headerType: SystemMetric['headerType'] | 'custom_metric';
  // UI state (already mapped, limit reached, deactivated in MDM, etc)
  disabled?: boolean;
};

// Hidden option that keeps the custom metrics group (and its empty state)
// in the dropdown when there are no custom metrics
const EMPTY_CUSTOM_METRICS_OPTION = {
  name: '__empty_custom_metrics__',
  label: '',
  headerType: 'custom_metric',
  required: false,
} as const;

type EmptyCustomMetricsOption = typeof EMPTY_CUSTOM_METRICS_OPTION;

const isEmptyCustomMetricsOption = (
  option: HeaderOption | EmptyCustomMetricsOption,
): option is EmptyCustomMetricsOption => option === EMPTY_CUSTOM_METRICS_OPTION;

type MetricAutocompleteProps<Value extends HeaderOption> = {
  defaultValue?: Value | null;
  disabled?: boolean;
  onChange: (_: unknown, value: Value | null) => void;
  options: Value[];
  granularityLevel: GranularityLevel;
  value?: Value | null;
};

const renderMetricOptionExtra = (
  option: HeaderOption | EmptyCustomMetricsOption,
) => option.required && <span className="mr-auto -ml-1">*</span>;

export const MetricAutocomplete = <Value extends HeaderOption>({
  defaultValue,
  disabled,
  onChange,
  options,
  granularityLevel,
  value,
}: MetricAutocompleteProps<Value>) => {
  const { t } = useTranslation('@myn/import-hub');

  const metricTypes = useLocalizedMetricTypesLabels();
  const selectedOption = value ?? defaultValue ?? null;
  const [searchValue, setSearchValue] = useState('');

  const filteredOptions = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLocaleLowerCase();
    const matchingOptions = normalizedSearch
      ? options.filter((option) =>
          option.label.toLocaleLowerCase().includes(normalizedSearch),
        )
      : options;
    const hasCustomMetrics = options.some(
      (option) => option.headerType === 'custom_metric',
    );

    return hasCustomMetrics
      ? matchingOptions
      : [...matchingOptions, EMPTY_CUSTOM_METRICS_OPTION];
  }, [options, searchValue]);

  const handleClear = () => {
    setSearchValue('');
    if (selectedOption) {
      onChange(null, null);
    }
  };

  return (
    <div className="w-full">
      <Autocomplete<Value | EmptyCustomMetricsOption>
        key={selectedOption?.name ?? 'empty'}
        options={filteredOptions}
        size="small"
        autoFocus={false}
        disabled={disabled}
        color={selectedOption?.isDisabled ? 'error' : 'default'}
        defaultValue={selectedOption?.label ?? ''}
        getOptionLabel={(option) => option.label}
        isSelectedOption={(option) => option.name === selectedOption?.name}
        onAutocompleteChange={(option) => {
          if (isEmptyCustomMetricsOption(option) || option.disabled) {
            return;
          }

          setSearchValue('');
          onChange(null, option);
        }}
        formatAutocompleteValue={(value) => value}
        onClear={handleClear}
      >
        <AutocompleteTrigger fullWidth>
          <AutocompleteInput<Value | null>
            placeholder={t('Select')}
            selectedOption={selectedOption}
            onAutocompleteValueChange={setSearchValue}
            autoComplete="off"
          />
        </AutocompleteTrigger>
        <AutocompleteContent<Value | EmptyCustomMetricsOption>
          noOptionsText={t('No options')}
          groupBy={(option) => option.headerType}
          getOptionHidden={isEmptyCustomMetricsOption}
          renderGroupHeader={(group, groupOptions) => {
            const hasOptions = groupOptions.some(
              (option) => !isEmptyCustomMetricsOption(option),
            );

            return (
              <MetricsGroup
                group={group as HeaderOption['headerType']}
                metricTypes={metricTypes}
                hasOptions={hasOptions}
                granularityLevel={granularityLevel}
              />
            );
          }}
          getOptionExtra={renderMetricOptionExtra}
        />
      </Autocomplete>
    </div>
  );
};

type MetricsGroupProps = {
  group: HeaderOption['headerType'];
  metricTypes: Record<HeaderOption['headerType'], string>;
  hasOptions: boolean;
  granularityLevel: GranularityLevel;
};

const MetricsGroup = ({
  group,
  metricTypes,
  hasOptions,
  granularityLevel,
}: MetricsGroupProps) => {
  const { t } = useTranslation('@myn/import-hub');
  const isImportHubV2 = useImportHubV2();

  const isPricingItemLevel = granularityLevel === 'pricing_item';

  const isSystemMetricsGroup = group === 'metric';
  const isCustomMetricsGroup = group === 'custom_metric';

  const isRequiredGroup =
    (isPricingItemLevel && isSystemMetricsGroup) ||
    (!isPricingItemLevel && isCustomMetricsGroup);
  const hasGroupTooltip = isSystemMetricsGroup || !isImportHubV2;

  return (
    <div className="px-3 py-4">
      <div className="flex items-center">
        <span className={typographyVariants({ variant: 'overline1' })}>
          {metricTypes[group]}
        </span>
        {(isSystemMetricsGroup || isCustomMetricsGroup) && (
          <>
            {isRequiredGroup && (
              <>
                {hasGroupTooltip && (
                  <Tooltip
                    asChild
                    description={
                      isSystemMetricsGroup
                        ? t(
                            'Enhance your data analysis by exploring campaign Reporting through our database metrics.',
                          )
                        : t(
                            'Create and manage custom metrics to tailor data tracking to your specific needs.',
                          )
                    }
                  >
                    <div className="mr-1 ml-2 inline-flex">
                      <Information className="fill-icon-secondary" />
                    </div>
                  </Tooltip>
                )}
                <span
                  className={cn(
                    typographyVariants({ variant: 'label01' }),
                    'normal-case',
                    // The tooltip icon spaces the label from the group title
                    !hasGroupTooltip && 'ml-2',
                  )}
                >
                  {t('Choose at least one metric')}
                </span>
              </>
            )}
            {isCustomMetricsGroup && !isImportHubV2 && (
              <ManageCustomMetricsButton hasOptions={hasOptions} />
            )}
          </>
        )}
      </div>
      {isCustomMetricsGroup && !hasOptions && (
        <div className="pt-4">
          <span className={typographyVariants({ variant: 'label01' })}>
            {isImportHubV2
              ? t('No custom metrics available yet.')
              : t('No custom metrics available yet. Add some to proceed.')}
          </span>
        </div>
      )}
    </div>
  );
};
